import API_CONFIG from '@/config';
import base from '@/plugins/baseHttp';
import DocumentService from '@/features/documents/services';
import {
  processOwnerResponse,
  processPledgeResponse,
  processBurdenResponse,
  processAppraisalResponse,
  processMarketResearchResponse,
  processPropertyMarketResponse,
  formatPledge,
  formatOwner,
  formatBurden,
  formatAppraisal,
  formatMarketResearch,
  formatPropertyMarket,
} from './utils';
import {
  processAppraiserResponse,
} from '../appraiser/utils';

const PLEDGE_URL = `${API_CONFIG.API}/node/pledge`;
const OWNER_URL = `${API_CONFIG.API}/node/owner`;
const BURDEN_URL = `${API_CONFIG.API}/node/burden`;
const APPRAISER_BY_NAME_URL = `${API_CONFIG.API}/node/appraiser?
filter[name-filter][condition][path]=appraiser_name&
filter[name-filter][condition][operator]=STARTS_WITH&
filter[name-filter][condition][value]=`;
const APPRAISAL_URL = `${API_CONFIG.API}/node/appraisal`;
const MARKET_RESEARCH_URL = `${API_CONFIG.API}/node/market_research`;
const PROPERTY_MARKET_URL = `${API_CONFIG.API}/node/property_market`;
const PLEDGE_INCLUDES = '?include=owners,burdens,appraisals,property_market';
const MARKET_RESEARCH_INCLUDES = '?include=pdfs';
const PROPERTY_MARKET_INCLUDES = '?include=marker_researchs';
const APPRAISAL_INCLUDES = '?include=appraiser';
export default {
  getPledge(pledge) {
    return base.get(`${PLEDGE_URL}/${pledge.id}${PLEDGE_INCLUDES}`)
      .then(res => processPledgeResponse(res))
      .then((pledgeFromServer) => {
        const plegeResponse = pledgeFromServer;
        if (plegeResponse.propertyMarket &&
          plegeResponse.propertyMarket.id &&
          plegeResponse.propertyMarket.id !== 'dummy') {
          return this.getPropertyMarket(plegeResponse.propertyMarket)
            .then((propertyMarket) => {
              plegeResponse.propertyMarket = propertyMarket;
              return plegeResponse;
            });
        }
        return plegeResponse;
      });
  },
  getPropertyMarket(propertyMarket) {
    let propertyMarketResult;
    return base.get(`${PROPERTY_MARKET_URL}/${propertyMarket.id}${PROPERTY_MARKET_INCLUDES}`)
      .then(res => processPropertyMarketResponse(res))
      .then((properyMarketResponse) => {
        propertyMarketResult = properyMarketResponse;
        const promises = [];
        if (Array.isArray(properyMarketResponse.marketResearchs)) {
          properyMarketResponse.marketResearchs.forEach(mr => promises.push(this.getMarketResearch(mr)));
        }
        return Promise.all(promises);
      })
      .then((marketResearchResponses) => {
        propertyMarketResult.marketResearchs = marketResearchResponses;
        return propertyMarketResult;
      });
  },
  getMarketResearch(marketResearch) {
    let marketResearchResult;
    return base.get(`${MARKET_RESEARCH_URL}/${marketResearch.id}${MARKET_RESEARCH_INCLUDES}`)
      .then((res) => {
        const promises = [];
        marketResearchResult = processMarketResearchResponse(res);
        if (Array.isArray(marketResearchResult.pdfs)) {
          marketResearchResult.pdfs.forEach(pdf => promises.push(DocumentService.getDocument(pdf)));
        }
        return Promise.all(promises);
      })
      .then((pdfsResponse) => {
        marketResearchResult.pdfs = pdfsResponse;
        return marketResearchResult;
      });
  },
  updatePledge(pledge) {
    return base.patch(`${PLEDGE_URL}/${pledge.id}${PLEDGE_INCLUDES}`, { data: formatPledge(pledge) })
      .then(res => processPledgeResponse(res));
  },
  savePledge(pledge) {
    return base.post(`${PLEDGE_URL}${PLEDGE_INCLUDES}`, { data: formatPledge(pledge) })
      .then(res => processPledgeResponse(res));
  },
  removePledge(pledge) {
    return base.delete(`${PLEDGE_URL}/${pledge.id}`);
  },
  updateOwner(owner) {
    return base.patch(`${OWNER_URL}/${owner.id}`, { data: formatOwner(owner) })
      .then(res => processOwnerResponse(res));
  },
  saveOwner(pledge, owner) {
    let ownerModel = null;
    return base.post(`${OWNER_URL}`, { data: formatOwner(owner) })
      .then((ownerResponse) => {
        ownerModel = processOwnerResponse(ownerResponse);
        // TODO refactor proyect to ensure always [] when no relationships
        if ('owners' in pledge && pledge.owners.length >= 0) {
          pledge.owners.push(ownerModel);
        } else {
          pledge.owners = [ownerModel];
        }
        return base.patch(`${PLEDGE_URL}/${pledge.id}`, { data: formatPledge(pledge) });
      })
      .then(() => ownerModel);
  },
  removePledgeOwnerRelationship(pledge, owner) {
    if ('owners' in pledge && pledge.owners.length > 0) {
      pledge.owners = pledge.owners.filter(o => o.id !== owner.id);
    } else if ('owners' in pledge && pledge.owners.length === 0) {
      delete pledge.owners;
    }
    return base.patch(`${PLEDGE_URL}/${pledge.id}${PLEDGE_INCLUDES}`, { data: formatPledge(pledge) })
      .then(res => processPledgeResponse(res));
  },
  removeOwner(owner) {
    return base.delete(`${OWNER_URL}/${owner.id}`);
  },
  updateBurden(burden) {
    return base.patch(`${BURDEN_URL}/${burden.id}`, { data: formatBurden(burden) })
      .then(res => processBurdenResponse(res));
  },
  saveBurden(pledge, burden) {
    let burdenModel = null;
    return base.post(`${BURDEN_URL}`, { data: formatBurden(burden) })
      .then((burdenResponse) => {
        burdenModel = processBurdenResponse(burdenResponse);
        if ('burdens' in pledge && pledge.burdens.length >= 0) {
          pledge.burdens.push(burdenModel);
        } else {
          pledge.burdens = [burdenModel];
        }
        return base.patch(`${PLEDGE_URL}/${pledge.id}`, { data: formatPledge(pledge) });
      })
      .then(() => burdenModel);
  },
  removePledgeBurdenRelationship(pledge, burden) {
    if ('burdens' in pledge && pledge.burdens.length > 0) {
      pledge.burdens = pledge.burdens.filter(o => o.id !== burden.id);
    } else if ('burdens' in pledge && pledge.burdens.length === 0) {
      delete pledge.burdens;
    }
    return base.patch(`${PLEDGE_URL}/${pledge.id}${PLEDGE_INCLUDES}`, { data: formatPledge(pledge) })
      .then(res => processPledgeResponse(res));
  },
  removeBurden(burden) {
    return base.delete(`${BURDEN_URL}/${burden.id}`);
  },
  updateAppraisal(appraisal) {
    return base.patch(`${APPRAISAL_URL}/${appraisal.id}`, { data: formatAppraisal(appraisal) })
      .then(res => processAppraisalResponse(res));
  },
  saveAppraisal(pledge, appraisal) {
    let appraisalModel = null;
    return base.post(`${APPRAISAL_URL}`, { data: formatAppraisal(appraisal) })
      .then((appraisalResponse) => {
        appraisalModel = processAppraisalResponse(appraisalResponse);
        if ('appraisals' in pledge && pledge.appraisals.length >= 0) {
          pledge.appraisals.push(appraisalModel);
        } else {
          pledge.appraisals = [appraisalModel];
        }
        return base.patch(`${PLEDGE_URL}/${pledge.id}`, { data: formatPledge(pledge) });
      })
      .then(() => appraisalModel);
  },
  removePledgeAppraisalRelationship(pledge, appraisal) {
    if ('appraisals' in pledge && pledge.appraisals.length > 0) {
      pledge.appraisals = pledge.appraisals.filter(o => o.id !== appraisal.id);
    } else if ('appraisals' in pledge && pledge.appraisals.length === 0) {
      delete pledge.appraisals;
    }
    return base.patch(`${PLEDGE_URL}/${pledge.id}${PLEDGE_INCLUDES}`, { data: formatPledge(pledge) })
      .then(res => processPledgeResponse(res));
  },
  removeAppraisal(appraisal) {
    return base.delete(`${APPRAISAL_URL}/${appraisal.id}`);
  },
  updateMarketResearch(marketResearch) {
    return base.patch(`${MARKET_RESEARCH_URL}/${marketResearch.id}${MARKET_RESEARCH_INCLUDES}`, { data: formatMarketResearch(marketResearch) })
      .then(res => processMarketResearchResponse(res));
  },
  saveMarketResearch(propertyMarket, marketResearch) {
    let marketResearchModel = null;
    return base.post(`${MARKET_RESEARCH_URL}`, { data: formatMarketResearch(marketResearch) })
      .then((marketResearchResponse) => {
        marketResearchModel = processMarketResearchResponse(marketResearchResponse);
        if ('marketResearchs' in propertyMarket && propertyMarket.marketResearchs.length >= 0) {
          propertyMarket.marketResearchs.push(marketResearchModel);
        } else {
          propertyMarket.marketResearchs = [marketResearchModel];
        }
        return base.patch(`${PROPERTY_MARKET_URL}/${propertyMarket.id}`, { data: formatPropertyMarket(propertyMarket) });
      })
      .then(() => marketResearchModel);
  },
  removePropertyMarketMarketResearchRelationship(propertyMarket, marketResearch) {
    if ('marketResearchs' in propertyMarket && propertyMarket.marketResearchs.length > 0) {
      propertyMarket.marketResearchs = propertyMarket.marketResearchs.filter(mr => mr.id !== marketResearch.id);
    } else if ('marketResearchs' in propertyMarket && propertyMarket.marketResearchs.length === 0) {
      delete propertyMarket.marketResearchs;
    }
    return base.patch(`${PROPERTY_MARKET_URL}/${propertyMarket.id}`, { data: formatPropertyMarket(propertyMarket) })
      .then(res => processPropertyMarketResponse(res));
  },
  removeMarketResearch(marketResearch) {
    return base.delete(`${MARKET_RESEARCH_URL}/${marketResearch.id}`);
  },
  updatePropertyMarket(propertyMarket) {
    return base.patch(`${PROPERTY_MARKET_URL}/${propertyMarket.id}${PROPERTY_MARKET_INCLUDES}`, { data: formatPropertyMarket(propertyMarket) })
      .then(res => processPropertyMarketResponse(res));
  },
  savePropertyMarket(pledge, propertyMarket) {
    let propertyMarketModel = null;
    return base.post(`${PROPERTY_MARKET_URL}${PROPERTY_MARKET_INCLUDES}`, { data: formatPropertyMarket(propertyMarket) })
      .then((propertyMarketResponse) => {
        propertyMarketModel = processPropertyMarketResponse(propertyMarketResponse);
        pledge.propertyMarket = propertyMarketModel;
        return base.patch(`${PLEDGE_URL}/${pledge.id}`, { data: formatPledge(pledge) });
      })
      .then(() => propertyMarketModel);
  },
  removePledgePropertyMarketRelationship(pledge) {
    if ('propertyMarket' in pledge) {
      pledge.propertyMarket = null;
    }
    return base.patch(`${PLEDGE_URL}/${pledge.id}${PLEDGE_INCLUDES}`, { data: formatPledge(pledge) })
      .then(res => processPledgeResponse(res));
  },
  removePropertyMarket(propertyMarket) {
    return base.delete(`${PROPERTY_MARKET_URL}/${propertyMarket.id}`);
  },
  searchAppraiser(appraiser) {
    return base.get(`${APPRAISER_BY_NAME_URL}${appraiser}`)
      .then(res => processAppraiserResponse(res));
  },
  getAppraiserByAppRaisal(id) {
    return base.get(`${APPRAISAL_URL}/${id}${APPRAISAL_INCLUDES}`);
  },
};



// WEBPACK FOOTER //
// ./src/features/tab-garantia/service.js